import create from 'zustand';
import {
  BondBuyPreviewResponse,
  BondProduct,
  BondSellPreviewResponse,
} from 'global/Securities/types/api/bond';
import {
  getBondBuyPreview,
  getBondSellPreview,
} from 'global/Securities/api/bond';

import { initialBuyPreview, initialSellPreview } from '../constants';

interface State {
  buy?: BondBuyPreviewResponse;
  sell?: BondSellPreviewResponse;
  product?: BondProduct;
  isLoading: boolean;
}

interface Actions {
  loadBuyPreview(productId: string, unit?: number): Promise<void>;
  loadSellPreview(
    productId: string,
    acquisitionOrderId: string,
    unit: number,
  ): Promise<void>;
  clear(): void;
}

type Store = State & Actions;

const initialState: State = {
  buy: initialBuyPreview,
  sell: initialSellPreview,
  product: undefined,
  isLoading: false,
};

export const useBondPreviewStore = create<Store>((set, get) => ({
  ...initialState,

  /**
   * We override the response when receive unit value <= 0
   *
   * This behavior is needed to provide the initial value of the buy modal
   *
   * We will show the 0 values to the user,
   *    except the buy price `price_rate` value.
   *
   */
  async loadBuyPreview(productId, unit = 0) {
    try {
      const isZeroUnit = unit <= 0;

      set({ isLoading: true });

      const response = await getBondBuyPreview({
        product_id: productId,
        /**
         * We will set the unit to 1 if the unit is 0 or negative value
         * So it will still calls the API without any errors
         */
        unit: isZeroUnit ? 1 : unit,
      });

      const { data } = response.data;

      if (isZeroUnit) {
        set({
          // we store the product information
          product: data.product,
          isLoading: false,
          // override response
          buy: {
            // set buy preview to the initial value (empty)
            ...initialState.buy,
            // exception, price_rate is needed to show the buying price
            price_rate: data.price_rate,
          },
        });
        return;
      }
      set({ buy: data, product: data.product, isLoading: false });
    } catch (error) {
      set({ product: initialState.product, isLoading: false });
      Promise.reject(error);
    }
  },

  async loadSellPreview(productId, acquisitionOrderId, unit = 0) {
    try {
      const isZeroUnit = unit <= 0;
      const priceRate = get().sell.price.sell;
      const availableUnit = get().sell.available_units;

      if (isZeroUnit && priceRate && availableUnit) {
        set({
          sell: {
            // set buy preview to the initial value (empty)
            ...initialState.sell,
            available_units: availableUnit,
            // exception, price.sell is needed to show the selling price
            price: {
              ...initialState.sell.price,
              sell: priceRate,
            },
          },
        });
        return;
      }

      if (!productId) {
        throw new Error('Product Code is required');
      }

      if (!acquisitionOrderId) {
        throw new Error('Order id is required');
      }

      set({ isLoading: true });

      const response = await getBondSellPreview({
        product_id: productId,
        acquisition_order_id: acquisitionOrderId,
        unit: isZeroUnit ? 1 : unit,
      });

      const { data } = response.data;

      if (isZeroUnit) {
        set({
          product: data.product,
          isLoading: false,
          // override the sell preview response with initial state
          sell: {
            ...initialState.sell,
            // exception, available unit
            available_units: data.available_units,
            price: {
              ...initialState.sell.price,
              // except the price sell data
              sell: data.price.sell,
            },
          },
        });
        return;
      }

      set({ sell: data, product: data.product, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      Promise.reject(error);
    }
  },

  clear() {
    set(initialState);
  },
}));
